import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { memo, useEffect } from "react";
import { Image, Linking } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { fetchCheckVersionAuthenticated } from "../actions";
import { fetchGetCommunityDetails } from "../actions/community";
import {
	LOGOUT,
	SET_INITIAL_LINK,
	SET_INITIAL_URL,
	SET_INSTALLATION_ID
} from "../actions/types";
import { getImage } from "../assets/Images";
import {
	ALL_ROUTES,
	APP_NAME,
	SELECTED_COMMUNITY_URI_KEY
} from "../config/constants";
import { getLoadingAppSelector } from "../selectors";
import commonStyles from "../styles/commonStyles";
import { getInstallationId } from "../utils/InstallationId";
import { getRedirectLink } from "../utils/UrlUtil";

const LoadingScreen = () => {
	const dispatch = useDispatch();

	const {
		initialUrl,
		clientVersion,
		serverVersion,
		currentCommunity,
		isNotFound
	} = useSelector(getLoadingAppSelector);

	const regex = /^\/([0-9a-zA-Z-]+)\/?(.*)/;

	const getCurrentCommunity = (link, communityUri) => {
		if (regex.test(link) && !ALL_ROUTES.has(link.match(regex)[1])) {
			dispatch(fetchGetCommunityDetails(link.match(regex)[1]));
		} else if (communityUri) {
			dispatch(fetchGetCommunityDetails(communityUri));
		} else {
			// No community in the url nor previously selected: no default community.
			// A logged in user gets the community of its session, the others are
			// invited to select their community on the landing screen.
			dispatch(fetchCheckVersionAuthenticated(undefined, undefined, false));
		}
	};

	const handleOpenURL = async ({ url }) => {
		const communityUri = await AsyncStorage.getItem(SELECTED_COMMUNITY_URI_KEY);
		const link = getRedirectLink(url) || "/";

		dispatch({
			type: SET_INITIAL_LINK,
			value: link
		});
		getCurrentCommunity(link, communityUri);
	};

	useEffect(() => {
		(async () => {
			Linking.addEventListener("url", handleOpenURL);
			if (!currentCommunity) {
				let installationId = await AsyncStorage.getItem(
					`${APP_NAME}InstallationId`
				);
				if (!installationId) {
					installationId = await getInstallationId();
					await AsyncStorage.setItem(
						`${APP_NAME}InstallationId`,
						installationId
					);
				}
				if (installationId) {
					dispatch({
						type: SET_INSTALLATION_ID,
						value: installationId
					});
				}
				if (!initialUrl) {
					const url = await Linking.getInitialURL();
					dispatch({
						type: SET_INITIAL_URL,
						value: url
					});
					handleOpenURL({ url });
				}
			}
		})();
	}, []);

	useEffect(() => {
		if (isNotFound) {
			// logout in case community has been removed
			dispatch({
				type: LOGOUT
			});
			dispatch(fetchGetCommunityDetails());
		}
	}, [isNotFound]);

	useEffect(() => {
		if (currentCommunity && !clientVersion) {
			let targetUserId = null;
			if (initialUrl) {
				try {
					const url = new URL(initialUrl);
					targetUserId = url.searchParams.get("targetUserId");
				} catch (error) {
					// Fallback pour les URLs relatives ou malformées
					const urlParams = new URLSearchParams(initialUrl.split("?")[1]);
					targetUserId = urlParams.get("targetUser");
				}
			}
			if (serverVersion) {
				// Workaround: wait a while before checking authentication
				// if we logout and call fetchCheckVersionAuthenticated quickly, the user session will be reused
				setTimeout(
					() =>
						dispatch(
							fetchCheckVersionAuthenticated(currentCommunity._id, targetUserId)
						),
					1000
				);
			} else {
				dispatch(
					fetchCheckVersionAuthenticated(currentCommunity._id, targetUserId)
				);
			}
		}
	}, [currentCommunity, clientVersion]);

	const { cover, w100p, h100p } = commonStyles;
	return <Image style={[cover, w100p, h100p]} source={getImage("splash")} />;
};

const MemoizedLoadingScreen = memo(LoadingScreen);
export default MemoizedLoadingScreen;
